/**
 * Vino Cammino six-pack checkout gate (Minmaxify replacement).
 *
 * Blocks checkout from the storefront (cart page, cart drawer, cart
 * notification) when the cart holds wine that is not a positive multiple of
 * six qualifying bottles, showing a popup instead. Non-wine-only carts
 * (gift cards, accessories, magnums) always check out freely.
 *
 * Single source of truth: the header badge (snippets/vc-six-badge.liquid +
 * assets/vc-six-badge.js) computes the count and keeps its data- attributes
 * current on every cart change. This gate only READS that state:
 *   - decision:   [data-vc-badge] data-q  (block iff q > 0 and q % 6 != 0)
 *   - popup copy: cloned live from the badge's tooltip, so gate and badge
 *     can never say different things.
 *
 * FAIL-OPEN BY DESIGN: if the badge is missing or unreadable, checkout is
 * allowed. A stray non-multiple order is a small fulfilment annoyance; code
 * silently blocking valid checkouts is lost revenue. Keep it this way.
 */
(function () {
  'use strict';

  var CHECKOUT_SELECTOR =
    'button[name="checkout"], input[name="checkout"], #checkout, #CartDrawer-Checkout';

  function badgeState() {
    var b = document.querySelector('[data-vc-badge]');
    if (!b) return null;
    var q = parseInt(b.dataset.q, 10);
    var remaining = parseInt(b.dataset.remaining, 10);
    if (isNaN(q)) return null;
    return { q: q, remaining: isNaN(remaining) ? 0 : remaining, el: b };
  }

  // Block only when wine is present and not a clean six. q === 0 (empty or
  // non-wine-only cart) must pass - those orders are always allowed.
  function shouldBlock() {
    var s = badgeState();
    if (!s) return false; // fail open
    return s.q > 0 && s.q % 6 !== 0;
  }

  /* ---- modal ---- */

  var lastFocus = null;

  function modal() {
    return document.getElementById('vc-gate-modal');
  }

  function openModal() {
    var m = modal();
    if (!m) return;
    var body = m.querySelector('.vc-gate__body');
    var tip = document.querySelector('[data-vc-badge] .vc-tip');
    if (body && tip) {
      // Clone the badge tooltip's current content: main line, rule line and
      // any conditional pack/extras lines - always in sync with the badge.
      body.innerHTML = '';
      body.appendChild(document.createTextNode(tip.childNodes[0] ? tip.childNodes[0].textContent : ''));
      for (var i = 1; i < tip.children.length + 1; i++) {
        var child = tip.children[i - 1];
        if (!child) continue;
        var p = document.createElement('span');
        p.className = child.classList.contains('vc-tip__sub') ? 'vc-gate__sub' : 'vc-gate__line';
        p.textContent = child.textContent;
        body.appendChild(p);
      }
    }
    lastFocus = document.activeElement;
    m.removeAttribute('hidden');
    var ok = m.querySelector('.vc-gate__ok');
    if (ok) ok.focus();
    document.addEventListener('keydown', onKeydown, true);
  }

  function closeModal() {
    var m = modal();
    if (!m) return;
    m.setAttribute('hidden', '');
    document.removeEventListener('keydown', onKeydown, true);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  function onKeydown(evt) {
    if (evt.key === 'Escape' || evt.code === 'Escape') {
      evt.preventDefault();
      closeModal();
    } else if (evt.key === 'Tab') {
      // single focusable control: keep focus on the OK button
      var ok = modal() && modal().querySelector('.vc-gate__ok');
      if (ok) {
        evt.preventDefault();
        ok.focus();
      }
    }
  }

  /* ---- checkout interception (capture phase, before theme handlers) ---- */

  function onDocumentClick(evt) {
    var target = evt.target && evt.target.closest ? evt.target.closest(CHECKOUT_SELECTOR) : null;
    if (!target) return;
    if (!shouldBlock()) return; // valid or fail-open: let the theme proceed
    evt.preventDefault();
    evt.stopPropagation();
    openModal();
  }

  // Belt-and-braces: catch form submissions that carry the checkout intent
  // even if the click listener was somehow bypassed (e.g. Enter key paths).
  function onDocumentSubmit(evt) {
    var form = evt.target;
    if (!form || !form.action || form.action.indexOf('/cart') === -1) return;
    var submitter = evt.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (!shouldBlock()) return;
    evt.preventDefault();
    evt.stopPropagation();
    openModal();
  }

  /* ---- express checkout buttons (G Pay etc.) in cart surfaces ----
     Hidden while the cart is invalid, mirroring Minmaxify's behaviour.
     Driven by the badge's data-state via MutationObserver, so it stays in
     sync through JS updates AND section re-renders. */

  function syncExpressButtons() {
    var block = shouldBlock();
    document.documentElement.classList.toggle('vc-gate-invalid', block);
  }

  function observeBadge() {
    var host = document.querySelector('#cart-icon-bubble') || document.body;
    new MutationObserver(syncExpressButtons).observe(host, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-q', 'data-state'],
    });
  }

  function init() {
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('submit', onDocumentSubmit, true);
    var m = modal();
    if (m) {
      m.addEventListener('click', function (evt) {
        if (evt.target.classList.contains('vc-gate__overlay') || evt.target.closest('.vc-gate__ok')) {
          closeModal();
        }
      });
    }
    if (typeof MutationObserver !== 'undefined') observeBadge();
    syncExpressButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
